import type { CreatedPayload } from '@/widgets/notifications-realtime/useNotificationsRealtime';
import { createContext, useContext, useEffect, useRef } from 'react';

export interface NotificationDeleteResult {
    ok?: boolean;
    deleted_count?: number;
    unread_count?: number;
}

export interface NotificationsContextValue {
    unreadCount: number;
    markRead: (id: string) => Promise<number | null>;
    markAllRead: () => Promise<number | null>;
    deleteOne: (id: string) => Promise<NotificationDeleteResult | null>;
    deleteAllRead: () => Promise<NotificationDeleteResult | null>;
    /** Register a listener for realtime `notification.created` events (per-consumer list updates). Returns an unsubscribe fn. */
    subscribeCreated: (handler: (payload: CreatedPayload) => void) => () => void;
}

export const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotifications(): NotificationsContextValue {
    const ctx = useContext(NotificationsContext);
    if (!ctx) throw new Error('useNotifications must be used within a NotificationsProvider');
    return ctx;
}

/** Subscribe the calling component to realtime-created notifications (for its own list). Auto-unsubscribes on unmount. */
export function useNotificationCreated(handler: (payload: CreatedPayload) => void): void {
    const { subscribeCreated } = useNotifications();
    const handlerRef = useRef(handler);
    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);
    useEffect(() => subscribeCreated((payload) => handlerRef.current(payload)), [subscribeCreated]);
}
